// Dev-session auth backend — used when no Cognito pool is configured. The API's /dev-login issues an
// httpOnly cookie scoped to the patient audience; the client sends it via credentials:"include".
// This keeps the full book/reschedule/cancel/join flow testable locally without Cognito provisioned.
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { API_ORIGIN } from "../config";
import { AuthContext, type AppAuth } from "./context";

export function DevAuthProvider({ children }: { children: ReactNode }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isSignedIn, setIsSignedIn] = useState(false);

  const probe = useCallback(async () => {
    try {
      const res = await fetch(`${API_ORIGIN}/v1/patient/me`, { credentials: "include" });
      setIsSignedIn(res.ok);
    } catch {
      setIsSignedIn(false);
    } finally {
      setIsLoaded(true);
    }
  }, []);

  useEffect(() => {
    void probe();
  }, [probe]);

  const value = useMemo<AppAuth>(
    () => ({
      isLoaded,
      isSignedIn,
      displayName: isSignedIn ? "Member (dev)" : null,
      getToken: async () => null, // cookie-based; no bearer token in dev
      signOut: () => setIsSignedIn(false),
      updateProfile: async () => {}, // dev has no Cognito identity; the backend PUT is the only source
      devSignIn: async () => {
        await fetch(`${API_ORIGIN}/dev-login?as=patient`, { credentials: "include" });
        await probe();
      },
    }),
    [isLoaded, isSignedIn, probe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
